import {useState, useMemo} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {TextField, InputAdornment, Tooltip} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import HistoryIcon from '@mui/icons-material/History'
import PropTypes from 'prop-types'
import {StyledTooltip} from '../../BreadCrumb/BreadCrumbStyle'
import {useRecentSearches} from '../../../hooks/useRecentSearches'
import {useGlobalSearch} from '../../../store/hooks/globalSearch'
import {
  toggleFilter,
  selectAddedFilters,
  selectDisabledFilters,
  selectSelectedState,
  selectBenefitsEligibility,
  setFilterValue
} from '../../../store/slices/filterManagementSlice'
import {getDisabledFilterTooltip, getFilterType, getFilterConfig} from '../../../utils/filterManagementHelpers'
import {selectFilterConfig} from '../../../store/slices/configSlice'
import {AnalyticsHandler} from '../../../shared/handlers/analytics/handlers'
import {
  StyledAutocomplete,
  StyledNoResultsText,
  StyledHelpText,
  StyledListItemContent,
  StyledHighlightedText,
  StyledNoResultsContainer,
  StyledSearchText,
  StyledFlexContainer,
  StyledIcon,
  StyledInfoIcon,
  StyledAddedBadge,
  StyledClearIconButton,
  StyledPaper,
  StyledAdornmentClearIconButton,
  StyledColoredIcon,
  StyledStartAdornmentIcon,
  StyledTitle,
  listBoxPropStyles
} from './GlobalSearchFilterStyles'

const AutocompletePaper = (props) => {
  return <StyledPaper {...props} elevation={3} />
}

const GlobalSearchFilter = ({
  placeholder = 'Search all filters',
  ariaLabel = 'Search all filters',
  id = 'global-search'
}) => {
  const dispatch = useDispatch()
  const [value, setValue] = useState(null)
  const [inputValue, setInputValue] = useState('')
  const [isInputFocused, setIsInputFocused] = useState(false)
  const {recentSearches, addRecentSearch, removeRecentSearch} = useRecentSearches()
  const {globalSearchFilterOptions} = useGlobalSearch()
  const addedFilters = useSelector(selectAddedFilters)
  const disabledFilters = useSelector(selectDisabledFilters)
  const selectedState = useSelector(selectSelectedState)
  const filterConfig = useSelector(selectFilterConfig)
  const benefitsEligibility = useSelector(selectBenefitsEligibility)

  // Enrich options with dynamic isAdded/isDisabled state and proper tooltip text
  const options = useMemo(() => {
    return globalSearchFilterOptions.map((option) => {
      if (option.filterKey) {
        const isAdded = addedFilters.includes(option.filterKey)
        const isDisabled = disabledFilters.includes(option.filterKey)
        const disabledTooltip = isDisabled
          ? getDisabledFilterTooltip(option.filterKey, selectedState, filterConfig)
          : null

        return {
          ...option,
          isAdded,
          isDisabled,
          selected: isAdded,
          disabled: option.disabled || isDisabled,
          tooltipText: option.tooltipText || disabledTooltip
        }
      }
      return option
    })
  }, [globalSearchFilterOptions, addedFilters, disabledFilters, selectedState, filterConfig])

  const handleClear = () => {
    setValue(null)
    setInputValue('')
  }

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue)
  }

  const handleOptionChange = (event, newValue) => {
    if (newValue.disabled || newValue.visible || newValue.tooltip || newValue.selected) {
      return
    }
    handleOptionSelection(newValue)
  }

  const handleOutreachSimulationSelection = (option) => {
    const filterKey = option.filterKey
    const currentValue = benefitsEligibility

    if (!addedFilters.includes(filterKey)) {
      const filterType = getFilterType(filterKey, filterConfig)
      dispatch(
        toggleFilter({
          key: filterKey,
          categoryId: option.categoryId,
          type: filterType
        })
      )
    }

    const currentSelected = currentValue?.selected || []

    const newSelected = currentSelected.includes(option.value) ? currentSelected : [...currentSelected, option.value]

    const enrolledValue = currentValue?.enrolledValue === undefined ? 2 : currentValue.enrolledValue

    // Update the filter value with new selection
    dispatch(
      setFilterValue({
        key: filterKey,
        value: {
          selected: newSelected,
          enrolledValue
        }
      })
    )

    // Send analytics for outreach simulation filter selection
    // Get category and filter names for analytics
    const category = filterConfig?.data?.find((cat) => cat.id === option.categoryId)
    const filterConfigData = getFilterConfig(filterKey, filterConfig)

    AnalyticsHandler.onFilterApply({
      name: option.label, // e.g., "SNAP"
      category: category?.title,
      subCategory: filterConfigData?.name
    })
  }

  const handleUnifiedFilterSelection = (option) => {
    const filterType = getFilterType(option.filterKey, filterConfig)
    dispatch(
      toggleFilter({
        key: option.filterKey,
        categoryId: option.categoryId,
        type: filterType
      })
    )
    const category = filterConfig?.data?.find((cat) => cat.id === String(option.categoryId))

    AnalyticsHandler.onFilterApply({
      name: option.title,
      category: category?.title || 'Unknown Category'
    })
  }

  const handleOptionSelection = (option) => {
    if (option.disabled || option.visible) return

    if (option.section === 'OS') {
      handleOutreachSimulationSelection(option)
    } else if (option.filterKey) {
      handleUnifiedFilterSelection(option)
    }

    // For outreach simulation, use the label (e.g., 'SNAP'), otherwise use filterKey
    const searchKey = option.section === 'OS' ? option.label : option.filterKey
    addRecentSearch(searchKey)
    setValue(null)
    setInputValue('')
    document.getElementById(id)?.blur()
  }

  const handleClearRecentSearch = (e, filterName) => {
    e.stopPropagation()
    removeRecentSearch(filterName)
  }

  const getAddedBadge = (option) => {
    return option.isAdded || option.selected ? <StyledAddedBadge aria-label={`, Added,`}>Added</StyledAddedBadge> : null
  }

  const getInfoIconWithTooltip = (option) => {
    return option.tooltip || (option.tooltipText && option.disabled) ? (
      <StyledTooltip
        arrow
        title={option.tooltipText}
        placement='bottom'
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [20, -5]
              }
            }
          ]
        }}
      >
        <StyledInfoIcon fontSize='small' />
      </StyledTooltip>
    ) : null
  }
  return (
    <StyledAutocomplete
      id={id}
      value={value}
      onChange={handleOptionChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      disableClearable
      forcePopupIcon={false}
      PaperComponent={AutocompletePaper}
      open={isInputFocused && (recentSearches?.length > 0 || inputValue.length >= 3)}
      aria-expanded={isInputFocused && (recentSearches?.length > 0 || inputValue.length >= 3)}
      getOptionLabel={(option) => {
        return option.filterName
      }}
      filterOptions={(x) => x}
      options={
        inputValue.length >= 3
          ? (() => {
              const filtered = options.filter((option) => {
                const optStr = option.filterName.toLowerCase()
                return optStr.includes(inputValue.toLowerCase())
              })
              return filtered.length > 0 ? filtered : []
            })()
          : [
              {label: 'recent-searches-header', title: 'Recent Searches', isHeader: true, disabled: true},
              ...(recentSearches?.map((filterName) => {
                const optionObj = options.find((obj) => obj.label === filterName)
                return {
                  ...optionObj,
                  type: 'recent'
                }
              }) || [])
            ]
      }
      noOptionsText={
        inputValue.length >= 3 ? (
          <StyledNoResultsContainer>
            <StyledNoResultsText color='error'>No results found</StyledNoResultsText>
            <StyledHelpText variant='subtitle2'>Check the spelling or try less specific search terms</StyledHelpText>
          </StyledNoResultsContainer>
        ) : null
      }
      renderOption={(props, option, {inputValue}) => {
        if (option?.isHeader) {
          return <StyledTitle>{option.title}</StyledTitle>
        }
        if (option?.type === 'recent') {
          return (
            <li {...props}>
              <StyledListItemContent>
                <StyledFlexContainer>
                  <StyledIcon component={HistoryIcon} aria-hidden='true' />
                  <span>{option.filterName}</span>
                </StyledFlexContainer>
                <StyledFlexContainer>
                  {getAddedBadge(option)}
                  {getInfoIconWithTooltip(option)}
                  <Tooltip title='Remove'>
                    <StyledClearIconButton
                      size='small'
                      onClick={(event) => handleClearRecentSearch(event, option.label)}
                      aria-label={`, Remove Filter,`}
                    >
                      <StyledColoredIcon component={ClearIcon} fontSize='small' />
                    </StyledClearIconButton>
                  </Tooltip>
                </StyledFlexContainer>
              </StyledListItemContent>
            </li>
          )
        }
        const optionText = option?.filterName.toString()
        const matchText = inputValue.toLowerCase()
        const matchStartIndex = optionText.toLowerCase().indexOf(matchText)
        const matchEndIndex = matchStartIndex + inputValue.length
        return (
          <li {...props}>
            <StyledListItemContent>
              <StyledFlexContainer>
                <StyledIcon component={SearchIcon} />
                <StyledSearchText component='span'>
                  {optionText.substring(0, matchStartIndex)}
                  <StyledHighlightedText>{optionText.substring(matchStartIndex, matchEndIndex)}</StyledHighlightedText>
                  {optionText.substring(matchEndIndex)}
                </StyledSearchText>
              </StyledFlexContainer>
              <StyledFlexContainer>
                {getAddedBadge(option)}
                {getInfoIconWithTooltip(option)}
              </StyledFlexContainer>
            </StyledListItemContent>
          </li>
        )
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          aria-label={ariaLabel}
          placeholder={placeholder}
          variant='outlined'
          size='medium'
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
          fullWidth
          id={`${id}-input`}
          InputLabelProps={{
            id: `${id}-label`,
            shrink: true
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment:
              inputValue.length > 0 ? (
                <InputAdornment position='start'>
                  <StyledStartAdornmentIcon component={SearchIcon} aria-hidden='true' />
                </InputAdornment>
              ) : null,
            endAdornment: (
              <>
                {inputValue.length > 0 && (
                  <InputAdornment position='end'>
                    <StyledAdornmentClearIconButton
                      aria-label='Clear search'
                      onClick={handleClear}
                      size='small'
                      tabIndex={0}
                    >
                      <StyledColoredIcon component={ClearIcon} aria-hidden='true' />
                    </StyledAdornmentClearIconButton>
                  </InputAdornment>
                )}{' '}
                {inputValue.length === 0 && (
                  <InputAdornment position='end'>
                    <StyledIcon component={SearchIcon} aria-hidden='true' />
                  </InputAdornment>
                )}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
      ListboxProps={{
        'aria-labelledby': `${id}-label`,
        role: 'listbox',
        sx: listBoxPropStyles
      }}
    />
  )
}

GlobalSearchFilter.propTypes = {
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
  id: PropTypes.string
}

export default GlobalSearchFilter

/**
 * List of functions and their purpose:
 *
 * - handleClear: Resets search state by clearing value and input fields.
 * - handleInputChange: Updates input field value as user types in the search box.
 * - handleOptionChange: Prevents selection of disabled options and handles valid option selection.
 * - handleOptionSelection: Processes selected option using unified filterManagement system (toggleFilter action) for all filters including Outreach Simulation.
 * - handleOutreachSimulationSelection: Handles selection of outreach simulation filter options by updating filter values via setFilterValue action.
 * - handleUnifiedFilterSelection: Handles selection of standard filter options by toggling them via toggleFilter action.
 * - handleClearRecentSearch: Removes item from recent searches while preventing event propagation.
 * - getAddedBadge: Returns visual badge element when option is already added to filter drawer (checks isAdded property).
 * - getInfoIconWithTooltip: Returns tooltip-wrapped info icon for options with additional information.
 */
